import React from 'react';

import {
  List as MuiList,
  ListItem as MuiListItem,
  ListItemIcon,
} from '@mui/material';
import { ListProps as MuiListProps } from '@mui/material/List';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { Typography } from '../Typography';

export type ListItem = {
  className?: string;
  key: string;
  leftIcon?: React.ReactNode;
  onClick?: (e: any) => void;
  primaryContent: React.ReactNode | string;
  rightIcon?: React.ReactNode;
  status?: 'checked' | 'default';
  testId?: string;
  value?: string | number;
};

export interface ListProps extends Omit<MuiListProps, 'color'> {
  children?: React.ReactNode;
  items: ListItem[];
  testId?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  list: {
    paddingBottom: theme.spacing(2.5),
  },

  clickable: {
    cursor: 'pointer',

    '&:hover': {
      borderTop: `1px solid ${theme.palette.primary.main}`,

      '& + li': {
        borderTop: `1px solid ${theme.palette.primary.main}`,
      },

      '& + div': {
        borderTop: `1px solid ${theme.palette.primary.main}`,
      },
    },

    '&:last-child:hover:not($checked)': {
      borderBottom: `1px solid ${theme.palette.primary.main}`,
    },
  },

  content: {
    flex: '1 1 auto',
    margin: theme.spacing(0.5, 1.5),
    minWidth: 0,
    textAlign: 'left',
  },

  listItem: {
    alignItems: 'center',
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    display: 'flex',
    justifyContent: 'space-between',
    paddingLeft: 0,
    paddingRight: 0,

    '&:last-child:not($checked)': {
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },

    '& .MuiListItemIcon-root': {
      minWidth: 0,
    },
  },

  checked: {
    border: `2px solid ${theme.palette.primary.main}`,
  },

  wrapper: {
    paddingBottom: theme.spacing(2.5),
  },
}));

export function List(props: ListProps): JSX.Element {
  const classes = useStyles();
  const { className = '', items, testId: listTestId, ...rest } = props;

  const getTestId = (itemTestId, testListId, index): string => {
    let testId = '';

    if (itemTestId) {
      testId = itemTestId;
    } else if (testListId) {
      testId = `${testListId}-item-${index}`;
    }
    return testId;
  };

  const listStyles = clsx('fd-list', classes.list, className || '');

  const renderPrimaryContent = (content) => (
    <div className={classes.content}>
      {typeof content === 'string' ? (
        <Typography>{content}</Typography>
      ) : (
        content
      )}
    </div>
  );

  const renderListItem = (item: ListItem, index: number): JSX.Element => {
    const {
      key,
      leftIcon,
      onClick,
      primaryContent,
      rightIcon,
      status = '',
      testId: itemTestId,
    } = item;

    const listItemStyles = clsx(
      classes.listItem,
      'fd-list__item',
      onClick && classes.clickable,
      status === 'checked' && classes.checked,
    );

    const onClickProp = onClick && { onClick };
    const listItemTestId = getTestId(itemTestId, listTestId, index);
    const testIdProp = listItemTestId && { 'data-fd': listItemTestId };

    return (
      <MuiListItem
        button
        className={listItemStyles}
        key={key}
        role="listitem"
        {...onClickProp}
        {...testIdProp}
      >
        {leftIcon && <ListItemIcon>{leftIcon}</ListItemIcon>}

        {renderPrimaryContent(primaryContent)}

        {rightIcon && <ListItemIcon>{rightIcon}</ListItemIcon>}
      </MuiListItem>
    );
  };

  if (!items?.length) {
    return null;
  }

  return (
    <MuiList data-fd={listTestId} className={listStyles} {...rest}>
      {items.map((item: ListItem, index: number) =>
        renderListItem(item, index),
      )}
    </MuiList>
  );
}
